import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Col,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";

import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import classnames from "classnames";

function InventariosSeparados() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = import.meta.env.VITE_URL_INVENTARIOS;
  const URL_ARTICULOS = import.meta.env.VITE_URL_ARTICULOS;
  const URL_SEND_MAIL_ATT = import.meta.env.VITE_URL_SEND_MAIL_ATT;

  const [inventarioData, setInventarioData] = useState({ bodegas: [], tiendas: [] });
  const [loading, setLoading] = useState(true);
  const [articulos, setArticulos] = useState([]);
  const [selectedArticulo, setSelectedArticulo] = useState("");

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  // Tabs
  const [activeTab, setActiveTab] = useState("1");
  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Collapse state for each area
  const [openAreas, setOpenAreas] = useState({});
  const toggleArea = (areaId) => {
    setOpenAreas((prev) => ({
      ...prev,
      [areaId]: !prev[areaId],
    }));
  };

  // Search
  const [search, setSearch] = useState("");

  const [loader, showLoader, hideLoader] = useFullPageLoader();

  useEffect(() => {
    cargarDatos();
  }, []);

  function cargarDatos() {
    setLoading(true);

    axios
      .get(`${URL_INVENTARIOS}DisponibleSeparado`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setInventarioData(response.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar el inventario separado",
        });
      });

    axios
      .get(URL_ARTICULOS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setArticulos(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  // Filtrar articulos por busqueda
  const filtrarArticulos = (articulos) => {
    if (!search) return articulos;
    return articulos.filter(
      (a) =>
        a.codigo?.toLowerCase().includes(search.toLowerCase()) ||
        a.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        a.color?.toLowerCase().includes(search.toLowerCase()) ||
        a.talla?.toLowerCase().includes(search.toLowerCase())
    );
  };

  // Calcular totales
  const calcularTotales = (articulos) => {
    return articulos.reduce(
      (acc, a) => ({
        inventario: acc.inventario + (a.inventario || 0),
        disponible: acc.disponible + (a.disponible || 0),
        pedido: acc.pedido + (a.pedido || 0),
      }),
      { inventario: 0, disponible: 0, pedido: 0 }
    );
  };

  // Totales generales
  const totalesBodegas = useMemo(() => {
    let total = { inventario: 0, disponible: 0, pedido: 0 };
    inventarioData.bodegas.forEach((bodega) => {
      const articulosFiltrados = filtrarArticulos(bodega.articulos || []);
      const totales = calcularTotales(articulosFiltrados);
      total.inventario += totales.inventario;
      total.disponible += totales.disponible;
      total.pedido += totales.pedido;
    });
    return total;
  }, [inventarioData.bodegas, search]);

  const totalesTiendas = useMemo(() => {
    let total = { inventario: 0, disponible: 0, pedido: 0 };
    inventarioData.tiendas.forEach((tienda) => {
      const articulosFiltrados = filtrarArticulos(tienda.articulos || []);
      const totales = calcularTotales(articulosFiltrados);
      total.inventario += totales.inventario;
      total.disponible += totales.disponible;
      total.pedido += totales.pedido;
    });
    return total;
  }, [inventarioData.tiendas, search]);

  function PDFTabla() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = "Logo-Sistemify-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);
    var img2 = new Image();
    img2.src = "logoInstitucion.png";
    doc.addImage(img2, "png", 140, 15, 38, 18);
    doc.text(`Inventario Separado por Ubicacion`, 20, 40);

    let startY = 50;

    // Bodegas
    doc.setFontSize(14);
    doc.text("BODEGAS", 20, startY);
    startY += 5;

    inventarioData.bodegas.forEach((bodega) => {
      const articulosFiltrados = filtrarArticulos(bodega.articulos || []);
      if (articulosFiltrados.length > 0) {
        const totales = calcularTotales(articulosFiltrados);
        const data = articulosFiltrados.map((a) => [
          a.codigo,
          a.nombre,
          a.color || "",
          a.talla || "",
          new Intl.NumberFormat("en-US").format(a.inventario || 0),
          new Intl.NumberFormat("en-US").format(a.disponible || 0),
        ]);

        doc.setFontSize(12);
        doc.text(bodega.area, 20, startY + 5);

        doc.autoTable({
          head: [["Codigo", "Nombre", "Color", "Talla", "Inventario", "Disponible"]],
          body: data,
          startY: startY + 8,
          foot: [
            [
              "",
              "",
              "",
              "Total",
              new Intl.NumberFormat("en-US").format(totales.inventario),
              new Intl.NumberFormat("en-US").format(totales.disponible),
            ],
          ],
          showFoot: "lastPage",
          margin: { left: 20 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }
    });

    // Tiendas
    if (startY > 180) {
      doc.addPage();
      startY = 20;
    }
    doc.setFontSize(14);
    doc.text("TIENDAS", 20, startY);
    startY += 5;

    inventarioData.tiendas.forEach((tienda) => {
      const articulosFiltrados = filtrarArticulos(tienda.articulos || []);
      if (articulosFiltrados.length > 0) {
        const totales = calcularTotales(articulosFiltrados);
        const data = articulosFiltrados.map((a) => [
          a.codigo,
          a.nombre,
          a.color || "",
          a.talla || "",
          new Intl.NumberFormat("en-US").format(a.inventario || 0),
          new Intl.NumberFormat("en-US").format(a.disponible || 0),
        ]);

        if (startY > 180) {
          doc.addPage();
          startY = 20;
        }

        doc.setFontSize(12);
        doc.text(tienda.area, 20, startY + 5);

        doc.autoTable({
          head: [["Codigo", "Nombre", "Color", "Talla", "Inventario", "Disponible"]],
          body: data,
          startY: startY + 8,
          foot: [
            [
              "",
              "",
              "",
              "Total",
              new Intl.NumberFormat("en-US").format(totales.inventario),
              new Intl.NumberFormat("en-US").format(totales.disponible),
            ],
          ],
          showFoot: "lastPage",
          margin: { left: 20 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }
    });

    doc.save(`InventarioSeparado.pdf`);
  }

  function excel() {
    const dataExcel = [];

    // Bodegas
    inventarioData.bodegas.forEach((bodega) => {
      const articulosFiltrados = filtrarArticulos(bodega.articulos || []);
      articulosFiltrados.forEach((a) => {
        dataExcel.push({
          Tipo: "Bodega",
          Ubicacion: bodega.area,
          Codigo: a.codigo,
          Nombre: a.nombre,
          Color: a.color || "",
          Talla: a.talla || "",
          Inventario: a.inventario || 0,
          Disponible: a.disponible || 0,
        });
      });
    });

    // Tiendas
    inventarioData.tiendas.forEach((tienda) => {
      const articulosFiltrados = filtrarArticulos(tienda.articulos || []);
      articulosFiltrados.forEach((a) => {
        dataExcel.push({
          Tipo: "Tienda",
          Ubicacion: tienda.area,
          Codigo: a.codigo,
          Nombre: a.nombre,
          Color: a.color || "",
          Talla: a.talla || "",
          Inventario: a.inventario || 0,
          Disponible: a.disponible || 0,
        });
      });
    });

    var option = {};
    option.fileName = "InventarioSeparado";
    option.datas = [
      {
        sheetData: dataExcel,
        sheetName: "InventarioSeparado",
        sheetFilter: [
          "Tipo",
          "Ubicacion",
          "Codigo",
          "Nombre",
          "Color",
          "Talla",
          "Inventario",
          "Disponible",
        ],
        sheetHeader: [
          "Tipo",
          "Ubicacion",
          "Codigo",
          "Nombre",
          "Color",
          "Talla",
          "Inventario",
          "Disponible",
        ],
      },
    ];

    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function enviaMail() {
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = "Logo-Sistemify-PDF.png";
    doc.addImage(img, "png", 230, 30, 45, 15);
    var img2 = new Image();
    img2.src = "logoInstitucion.png";
    doc.addImage(img2, "png", 140, 15, 38, 18);
    doc.text(`Inventario Separado por Ubicacion`, 20, 40);

    let startY = 50;

    // Bodegas
    doc.setFontSize(14);
    doc.text("BODEGAS", 20, startY);
    startY += 5;

    inventarioData.bodegas.forEach((bodega) => {
      const articulosFiltrados = filtrarArticulos(bodega.articulos || []);
      if (articulosFiltrados.length > 0) {
        const totales = calcularTotales(articulosFiltrados);
        const data = articulosFiltrados.map((a) => [
          a.codigo,
          a.nombre,
          a.color || "",
          a.talla || "",
          new Intl.NumberFormat("en-US").format(a.inventario || 0),
          new Intl.NumberFormat("en-US").format(a.disponible || 0),
        ]);

        doc.setFontSize(12);
        doc.text(bodega.area, 20, startY + 5);

        doc.autoTable({
          head: [["Codigo", "Nombre", "Color", "Talla", "Inventario", "Disponible"]],
          body: data,
          startY: startY + 8,
          foot: [
            [
              "",
              "",
              "",
              "Total",
              new Intl.NumberFormat("en-US").format(totales.inventario),
              new Intl.NumberFormat("en-US").format(totales.disponible),
            ],
          ],
          showFoot: "lastPage",
          margin: { left: 20 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }
    });

    // Tiendas
    if (startY > 180) {
      doc.addPage();
      startY = 20;
    }
    doc.setFontSize(14);
    doc.text("TIENDAS", 20, startY);
    startY += 5;

    inventarioData.tiendas.forEach((tienda) => {
      const articulosFiltrados = filtrarArticulos(tienda.articulos || []);
      if (articulosFiltrados.length > 0) {
        const totales = calcularTotales(articulosFiltrados);
        const data = articulosFiltrados.map((a) => [
          a.codigo,
          a.nombre,
          a.color || "",
          a.talla || "",
          new Intl.NumberFormat("en-US").format(a.inventario || 0),
          new Intl.NumberFormat("en-US").format(a.disponible || 0),
        ]);

        if (startY > 180) {
          doc.addPage();
          startY = 20;
        }

        doc.setFontSize(12);
        doc.text(tienda.area, 20, startY + 5);

        doc.autoTable({
          head: [["Codigo", "Nombre", "Color", "Talla", "Inventario", "Disponible"]],
          body: data,
          startY: startY + 8,
          foot: [
            [
              "",
              "",
              "",
              "Total",
              new Intl.NumberFormat("en-US").format(totales.inventario),
              new Intl.NumberFormat("en-US").format(totales.disponible),
            ],
          ],
          showFoot: "lastPage",
          margin: { left: 20 },
        });
        startY = doc.lastAutoTable.finalY + 10;
      }
    });

    var att = doc.output("arraybuffer");
    var base64File = encode(att);

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Inventario Separado por Ubicacion",
          email: mailTo,
          fileName: "InventarioSeparado.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${import.meta.env.VITE_TAMANO_LOGO_MAIL}" align="center" src="${import.meta.env.VITE_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${import.meta.env.VITE_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontraras el inventario separado por ubicacion.</tr>
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${import.meta.env.VITE_TELEFONO_INSTITUCION}</tr>
         <tr align="right"><br>Powered by <a href="https://sistemify.com"> <img width="20%" align="center" src="${import.meta.env.VITE_LINK_SISTEMA}Logo-Sistemify-PDF.png" /> </a> </tr>
         </table>`,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        toggleMail();
        Swal.fire("Good job!", "Enviado con exito", "success");
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error.response?.data?.message || error.response?.data?.error || error.message}`,
        });
        console.log(error);
      });
  }

  // Renderizar tarjeta de area
  const renderAreaCard = (area, tipo) => {
    const articulosFiltrados = filtrarArticulos(area.articulos || []);
    const totales = calcularTotales(articulosFiltrados);
    const isOpen = openAreas[area.areaId] || false;

    return (
      <Card key={area.areaId} className="mb-2">
        <CardHeader
          onClick={() => toggleArea(area.areaId)}
          style={{ cursor: "pointer", backgroundColor: tipo === "bodega" ? "#e3f2fd" : "#fff3e0" }}
        >
          <Row className="align-items-center">
            <Col md={4}>
              <strong>
                <i className={`fas fa-${tipo === "bodega" ? "warehouse" : "store"} mr-2`}></i>
                {area.area}
              </strong>
            </Col>
            <Col md={2} className="text-center">
              <small className="text-muted">Articulos</small>
              <br />
              <Badge color="secondary">{articulosFiltrados.length}</Badge>
            </Col>
            <Col md={2} className="text-center">
              <small className="text-muted">Inventario</small>
              <br />
              <Badge color="info">{new Intl.NumberFormat("en-US").format(totales.inventario)}</Badge>
            </Col>
            <Col md={2} className="text-center">
              <small className="text-muted">Disponible</small>
              <br />
              <Badge color="success">{new Intl.NumberFormat("en-US").format(totales.disponible)}</Badge>
            </Col>
            <Col md={2} className="text-right">
              <i className={`fas fa-chevron-${isOpen ? "up" : "down"}`}></i>
            </Col>
          </Row>
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardBody style={{ maxHeight: "400px", overflowY: "auto" }}>
            {articulosFiltrados.length > 0 ? (
              <Table size="sm" striped borderless>
                <thead>
                  <tr>
                    <th>Codigo</th>
                    <th>Nombre</th>
                    <th>Color</th>
                    <th>Talla</th>
                    <th className="text-right">Inventario</th>
                    <th className="text-right">Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {articulosFiltrados.map((a, idx) => (
                    <tr key={idx}>
                      <td>{a.codigo}</td>
                      <td>{a.nombre}</td>
                      <td>{a.color || "-"}</td>
                      <td>{a.talla || "-"}</td>
                      <td className="text-right">
                        {new Intl.NumberFormat("en-US").format(a.inventario || 0)}
                      </td>
                      <td className="text-right">
                        {new Intl.NumberFormat("en-US").format(a.disponible || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-weight-bold">
                    <td colSpan={4} className="text-right">
                      TOTAL:
                    </td>
                    <td className="text-right">
                      {new Intl.NumberFormat("en-US").format(totales.inventario)}
                    </td>
                    <td className="text-right">
                      {new Intl.NumberFormat("en-US").format(totales.disponible)}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            ) : (
              <p className="text-muted text-center">No hay articulos que coincidan con la busqueda</p>
            )}
          </CardBody>
        </Collapse>
      </Card>
    );
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_disponible ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuInventarios"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" onClick={toggleMail} color="info">
                  eMail <i className="fas fa-at"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
            <ModalHeader toggle={toggleMail}>
              <h4>Enviar Inventario Separado</h4>
            </ModalHeader>
            <ModalBody>
              <Label className="mr-sm-2">Email</Label>
              <Input
                className="col-sm-12"
                type="text"
                value={mailTo}
                required
                onChange={(e) => {
                  setMailTo(e.target.value);
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button size="sm" color="success" onClick={enviaMail}>
                Enviar
              </Button>
            </ModalFooter>
          </Modal>

          <h3 align="center">Inventario por Ubicacion</h3>

          {/* Resumen General */}
          <Row className="mb-3">
            <Col md={6}>
              <Card className="bg-light">
                <CardBody className="py-2">
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-0">
                        <i className="fas fa-warehouse mr-2"></i>
                        Bodegas
                      </h5>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Ubicaciones</small>
                      <br />
                      <strong>{inventarioData.bodegas.length}</strong>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Inventario</small>
                      <br />
                      <strong className="text-info">
                        {new Intl.NumberFormat("en-US").format(totalesBodegas.inventario)}
                      </strong>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Disponible</small>
                      <br />
                      <strong className="text-success">
                        {new Intl.NumberFormat("en-US").format(totalesBodegas.disponible)}
                      </strong>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="bg-light">
                <CardBody className="py-2">
                  <Row className="align-items-center">
                    <Col>
                      <h5 className="mb-0">
                        <i className="fas fa-store mr-2"></i>
                        Tiendas
                      </h5>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Ubicaciones</small>
                      <br />
                      <strong>{inventarioData.tiendas.length}</strong>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Inventario</small>
                      <br />
                      <strong className="text-info">
                        {new Intl.NumberFormat("en-US").format(totalesTiendas.inventario)}
                      </strong>
                    </Col>
                    <Col className="text-center">
                      <small className="text-muted">Disponible</small>
                      <br />
                      <strong className="text-success">
                        {new Intl.NumberFormat("en-US").format(totalesTiendas.disponible)}
                      </strong>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Buscador */}
          <Row className="mb-3">
            <Col md={4}>
              <Input
                type="text"
                placeholder="Buscar por codigo, nombre, color o talla..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={8} className="text-right">
              <Button color="secondary" size="sm" onClick={() => cargarDatos()}>
                <i className="fas fa-sync-alt"></i> Actualizar
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center my-5">
              <i className="fas fa-spinner fa-spin fa-3x"></i>
              <p className="mt-2">Cargando inventario...</p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "1" })}
                    onClick={() => toggleTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-warehouse mr-1"></i>
                    Bodegas ({inventarioData.bodegas.length})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "2" })}
                    onClick={() => toggleTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-store mr-1"></i>
                    Tiendas ({inventarioData.tiendas.length})
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "3" })}
                    onClick={() => toggleTab("3")}
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fas fa-list mr-1"></i>
                    Todo
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="pt-3">
                {/* Tab Bodegas */}
                <TabPane tabId="1">
                  {inventarioData.bodegas.length > 0 ? (
                    inventarioData.bodegas.map((bodega) => renderAreaCard(bodega, "bodega"))
                  ) : (
                    <p className="text-muted text-center">No hay bodegas con inventario</p>
                  )}
                </TabPane>

                {/* Tab Tiendas */}
                <TabPane tabId="2">
                  {inventarioData.tiendas.length > 0 ? (
                    inventarioData.tiendas.map((tienda) => renderAreaCard(tienda, "tienda"))
                  ) : (
                    <p className="text-muted text-center">No hay tiendas con inventario</p>
                  )}
                </TabPane>

                {/* Tab Todo */}
                <TabPane tabId="3">
                  <h5 className="mb-3">
                    <i className="fas fa-warehouse mr-2"></i>Bodegas
                  </h5>
                  {inventarioData.bodegas.length > 0 ? (
                    inventarioData.bodegas.map((bodega) => renderAreaCard(bodega, "bodega"))
                  ) : (
                    <p className="text-muted text-center">No hay bodegas con inventario</p>
                  )}

                  <hr />

                  <h5 className="mb-3">
                    <i className="fas fa-store mr-2"></i>Tiendas
                  </h5>
                  {inventarioData.tiendas.length > 0 ? (
                    inventarioData.tiendas.map((tienda) => renderAreaCard(tienda, "tienda"))
                  ) : (
                    <p className="text-muted text-center">No hay tiendas con inventario</p>
                  )}
                </TabPane>
              </TabContent>
            </>
          )}
        </div>
      ) : (
        <div className="text-center">
          <h4>No tienes permiso para ver esta seccion</h4>
        </div>
      )}

      {loader}
    </>
  );
}

export default InventariosSeparados;
